import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography, Button, Avatar, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import palette from 'src/theme/palette';
import { AddSettings } from 'src/ApiServices';
import { toast } from 'react-toastify';
import { useLoading } from 'src/contexts/LoadingContext';

const LogoTab = ({ tabData = [], typeId }) => {
  const { refreshFavIcon } = useLoading();
  // Initialize logoData with id and value structure
  const getInitialLogoData = () => {
    const dataMap = {};
    tabData.forEach((item) => {
      dataMap[item.key] = {
        id: item.id || '',
        value: item.value || '',
        file: item.file || '',
        content_type: item.content_type || 'image',
        preview: item.file || ''
      };
    });

    return {
      transparent_header_logo: dataMap['transparent_header_logo'] || {
        id: '',
        value: '',
        file: null,
        content_type: 'image',
        preview: ''
      },
      sticky_header_logo: dataMap['sticky_header_logo'] || {
        id: '',
        value: '',
        file: null,
        content_type: 'image',
        preview: ''
      },
      footer_logo: dataMap['footer_logo'] || {
        id: '',
        value: '',
        file: null,
        content_type: 'image',
        preview: ''
      },
      crm_logo: dataMap['crm_logo'] || {
        id: '',
        value: '',
        file: null,
        content_type: 'image',
        preview: ''
      },
      website_crm_favicon: dataMap['website_crm_favicon'] || {
        id: '',
        value: '',
        file: null,
        content_type: 'image',
        preview: ''
      }
    };
  };

  const [logoData, setLogoData] = useState(getInitialLogoData());
  const [pendingUploads, setPendingUploads] = useState([]);

  useEffect(() => {
    setLogoData(getInitialLogoData());
  }, [tabData]);

  const uploadSingleImage = async (logoType, file) => {
    const fd = new FormData();

    fd.append('type_id', typeId);
    fd.append('key', logoType);
    fd.append('content_type', 'image');
    fd.append('file', file);
    fd.append('value', file.name);
    fd.append('id', logoData[logoType].id || '');

    console.log('Uploading: ', logoType);

    try {
      const res = await AddSettings(fd);

      setLogoData((prev) => ({
        ...prev,
        [logoType]: {
          ...prev[logoType],
          preview: URL.createObjectURL(file),
          file: file,
          value: file.name,
          id: res?.data?.id || prev[logoType].id
        }
      }));

      //  ONLY FOR FAVICON → trigger refresh
      if (logoType === 'website_crm_favicon') {
        refreshFavIcon();
      }

      // const res = await AddSettings(fd);
      // setLogoData((prev) => ({
      //   ...prev,
      //   [logoType]: {
      //     ...prev[logoType],
      //     preview: URL.createObjectURL(file),
      //     file: file,
      //     value: file.name,
      //     id: res?.data?.id || prev[logoType].id
      //   }
      // }));
      setPendingUploads((prev) => prev.filter((item) => item.logoType !== logoType));
    } catch (err) {
      console.error('Upload error:', err);
    }
  };

  const handleFileUpload = (logoType, event) => {
    const file = event.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setLogoData((prev) => ({
        ...prev,
        [logoType]: {
          ...prev[logoType],
          file: file,
          preview: previewUrl,
          value: file.name,
          content_type: 'image'
        }
      }));
      setPendingUploads((prev) => [...prev, { logoType, file }]);
      uploadSingleImage(logoType, file);
    }
  };

  const handleRemoveLogo = (logoType) => {
    setLogoData((prev) => ({
      ...prev,
      [logoType]: {
        ...prev[logoType],
        file: null,
        preview: '',
        value: ''
      }
    }));

    setPendingUploads((prev) => prev.filter((item) => item.logoType !== logoType));
  };

  const handleSaveSettings = async () => {
    if (pendingUploads.length > 0) {
      alert('Please wait for all uploads to complete before saving.');
      return;
    }

    const unuploadedLogos = [];
    Object.keys(logoData).forEach((key) => {
      if (logoData[key].file && !logoData[key].id) {
        unuploadedLogos.push({ logoType: key, file: logoData[key].file });
      }
    });

    if (unuploadedLogos.length > 0) {
      const uploadPromises = unuploadedLogos.map(({ logoType, file }) =>
        uploadSingleImage(logoType, file)
      );

      try {
        await Promise.all(uploadPromises);
        toast.success('All logos have been uploaded successfully!');
      } catch (error) {
        console.error('Error uploading logos:', error);
        toast.error('Some logos failed to upload. Please try again.');
      }
    } else {
      toast.success('All settings are up to date!');
    }
  };

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Transparent Header Logo
          </Typography>

          {logoData.transparent_header_logo?.preview && (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={logoData.transparent_header_logo.preview}
                variant="rounded"
                sx={{
                  width: 120,
                  height: 120,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLogo('transparent_header_logo')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            fullWidth
            sx={{ height: 100, borderStyle: 'dashed' }}
          >
            Upload Transparent Logo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload('transparent_header_logo', e)}
            />
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Sticky Header Logo
          </Typography>

          {logoData.sticky_header_logo?.preview && (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={logoData.sticky_header_logo.preview}
                variant="rounded"
                sx={{
                  width: 120,
                  height: 120,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLogo('sticky_header_logo')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            fullWidth
            sx={{ height: 100, borderStyle: 'dashed' }}
          >
            Upload Sticky Header Logo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload('sticky_header_logo', e)}
            />
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Footer Logo
          </Typography>

          {logoData.footer_logo?.preview && (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={logoData.footer_logo.preview}
                variant="rounded"
                sx={{
                  width: 120,
                  height: 120,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLogo('footer_logo')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            fullWidth
            sx={{ height: 100, borderStyle: 'dashed' }}
          >
            Upload Footer Logo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload('footer_logo', e)}
            />
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            CRM Logo
          </Typography>

          {logoData.crm_logo?.preview && (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={logoData.crm_logo.preview}
                variant="rounded"
                sx={{
                  width: 120,
                  height: 120,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLogo('crm_logo')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            fullWidth
            sx={{ height: 100, borderStyle: 'dashed' }}
          >
            Upload CRM Logo
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload('crm_logo', e)}
            />
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>
            Website/CRM Favicon
          </Typography>

          {logoData.website_crm_favicon?.preview && (
            <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
              <Avatar
                src={logoData.website_crm_favicon.preview}
                variant="rounded"
                sx={{
                  width: 120,
                  height: 120,
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveLogo('website_crm_favicon')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            component="label"
            fullWidth
            sx={{ height: 100, borderStyle: 'dashed' }}
          >
            Upload Favicon
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleFileUpload('website_crm_favicon', e)}
            />
          </Button>
        </Grid>

        <Grid item xs={12} container justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={pendingUploads.length > 0}
            sx={{
              background: palette.primary.main,
              color: palette.primary.contrastText,
              height: 40,
              '&:hover': { background: palette.primary.dark },
              '&.Mui-disabled': {
                background: palette.action.disabledBackground,
                color: palette.action.disabled
              }
            }}
          >
            {pendingUploads.length > 0 ? 'Uploading...' : 'Save Settings'}
          </Button>

          {pendingUploads.length > 0 && (
            <Typography variant="caption" sx={{ ml: 2, color: 'warning.main' }}>
              {pendingUploads.length} upload(s) in progress
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default LogoTab;
